import { Product } from '@akinon/next/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { COMPARE_CATEGORY_KEY } from '@theme/views/compare-products/category-attributes';

export const MAX_COMPARE_PRODUCTS = 3;

const getCompareCategory = (product: Product): string =>
  (product?.attributes?.[COMPARE_CATEGORY_KEY] as string) ?? '';

const getInitialProductsList = () => {
  if (typeof window !== 'undefined') {
    try {
      const storedProducts = JSON.parse(
        localStorage.getItem('compareProducts') || '[]'
      );
      return storedProducts;
    } catch (error) {
      console.error('Error parsing compareProducts from localStorage:', error);
      return [];
    }
  }
  return [];
};

const initialState = {
  productsPKList: [],
  isCompareOpen: false,
  limitWarning: false,
  compareCategory: '',
  categoryWarning: false
};

const compareSlice = createSlice({
  name: 'compare',
  initialState,
  reducers: {
    clearCompareList: (state) => {
      state.productsPKList = [];
      localStorage.removeItem('compareProducts');
      localStorage.removeItem('compareCategory');
      state.isCompareOpen = false;
      state.limitWarning = false;
      state.compareCategory = '';
      state.categoryWarning = false;
    },
    getCompareProducts: (state) => {
      const existingProducts = JSON.parse(
        localStorage.getItem('compareProducts') || '[]'
      );
      state.productsPKList = existingProducts;
      state.compareCategory = localStorage.getItem('compareCategory') || '';
    },
    addToCompare: (state, action: PayloadAction<Product>) => {
      if (state.productsPKList.length >= MAX_COMPARE_PRODUCTS) {
        state.limitWarning = true;
        return;
      }
      const productCategory = getCompareCategory(action.payload);
      if (state.productsPKList.length === 0) {
        state.compareCategory = productCategory;
        localStorage.setItem('compareCategory', productCategory);
      } else if (productCategory !== state.compareCategory) {
        state.categoryWarning = true;
        return;
      }
      state.categoryWarning = false;
      state.productsPKList.push(action.payload.pk);
      const existingProducts = JSON.parse(
        localStorage.getItem('compareProducts') || '[]'
      );
      const updatedProducts = [...existingProducts, action.payload.pk];
      localStorage.setItem('compareProducts', JSON.stringify(updatedProducts));
    },
    removeFromCompare: (state, action: PayloadAction<Product>) => {
      const productPkStr = String(action.payload.pk);

      state.productsPKList = state.productsPKList.filter(
        (pk) => String(pk) !== productPkStr
      );
      const existingProducts = JSON.parse(
        localStorage.getItem('compareProducts') || '[]'
      );
      const updatedProducts = existingProducts.filter(
        (pk: number | string) => String(pk) !== productPkStr
      );
      localStorage.setItem('compareProducts', JSON.stringify(updatedProducts));
      state.limitWarning = false;
      state.categoryWarning = false;
      if (state.productsPKList.length === 0) {
        state.compareCategory = '';
        localStorage.removeItem('compareCategory');
      }
    },
    toggleCompare: (state) => {
      state.isCompareOpen = !state.isCompareOpen;
    },
    setLimitWarning: (state, action: PayloadAction<boolean>) => {
      state.limitWarning = action.payload;
    },
    setCategoryWarning: (state, action: PayloadAction<boolean>) => {
      state.categoryWarning = action.payload;
    }
  }
});

export const {
  addToCompare,
  removeFromCompare,
  toggleCompare,
  getCompareProducts,
  clearCompareList,
  setLimitWarning,
  setCategoryWarning
} = compareSlice.actions;

export default compareSlice.reducer;
